// =====================================================
// CONFIG: Entity Logging Configuration
// Description: Configuration déclarative pour le logging automatique
// des changements d'entités dans la timeline
// =====================================================

import type { EntityType } from '../../types/timeline';
import {
  CREW_PROBABILITY_LABELS,
  CREW_STATUS_LABELS,
  CREW_FINANCING_TYPE_LABELS,
} from '../../types/crew';
import { CONTACT_STATUS_LABELS } from '../../types/contact';

/**
 * Configuration de logging pour un champ d'entité
 */
export interface FieldLoggingConfig {
  label: string; // Label métier (ex: "Email", "Téléphone")
  formatValue?: (value: any, relatedData?: any) => string; // Formater la valeur pour l'affichage
  isStatus?: boolean; // Si true, logger comme status_changed plutôt que data_updated
}

/**
 * Détecteur de changement métier
 * Permet de détecter des changements complexes (ex: plusieurs champs = "équipage inscrit")
 */
export interface StatusDetector {
  name: string; // Nom du changement métier (ex: "Équipage inscrit")
  priority: number; // Priorité (plus élevé = prioritaire). Pour gérer les cas multiples.
  detect: (oldData: any, newData: any) => boolean; // Fonction de détection
  description: (oldData: any, newData: any) => string; // Description du changement
}

/**
 * Configuration de logging pour une entité
 */
export interface EntityLoggingConfig {
  entityType: EntityType;
  // Champs à surveiller pour le logging
  trackedFields: Record<string, FieldLoggingConfig>;
  // Règles de détection de changements métier (optionnel)
  statusDetectors?: StatusDetector[];
}

// =====================================================
// CONFIGURATION CREW
// =====================================================

export const CREW_LOGGING_CONFIG: EntityLoggingConfig = {
  entityType: 'crew',
  trackedFields: {
    // Champs de base
    name: {
      label: "Nom de l'équipage",
    },
    crew_number: {
      label: "Numéro d'équipage",
      formatValue: (v) => v || 'Non renseigné',
    },

    // Champs de statut
    probability: {
      label: 'Probabilité',
      isStatus: true,
      formatValue: (v) => CREW_PROBABILITY_LABELS[v as keyof typeof CREW_PROBABILITY_LABELS] || v,
    },
    status: {
      label: 'Statut',
      isStatus: true,
      formatValue: (v) => CREW_STATUS_LABELS[v as keyof typeof CREW_STATUS_LABELS] || v,
    },

    // Champs métier importants
    budget: {
      label: 'Budget',
      formatValue: (v) => (v ? `${v}€` : 'N/A'),
    },
    financing_type: {
      label: 'Type de financement',
      formatValue: (v) =>
        CREW_FINANCING_TYPE_LABELS[v as keyof typeof CREW_FINANCING_TYPE_LABELS] || v,
    },

    // Relations
    product_id: {
      label: 'Produit',
      formatValue: (v, data) => {
        if (!v) return 'Aucun';
        return data?.product?.name || v;
      },
    },
    vehicle_id: {
      label: 'Véhicule',
      formatValue: (v, data) => {
        if (!v) return 'Aucun';
        return data?.vehicle?.registration || v;
      },
    },
    school_id: {
      label: 'École',
      formatValue: (v, data) => {
        if (!v) return 'Aucune';
        return data?.school?.name || v;
      },
    },
    association_id: {
      label: 'Association',
      formatValue: (v, data) => {
        if (!v) return 'Aucune';
        return data?.association?.name || v;
      },
    },
    discount_id: {
      label: 'Remise',
      formatValue: (v, data) => {
        if (!v) return 'Aucune';
        return data?.discount?.name || v;
      },
    },
    // assigned_to: géré manuellement via logAssignedUserChange dans useCRUD
    // pour avoir accès aux noms d'utilisateurs complets

    // Champs booléens importants
    has_derogation: {
      label: 'Dérogation',
      formatValue: (v) => (v ? 'Oui' : 'Non'),
    },
    organizes_pvd: {
      label: 'Organise PVD',
      formatValue: (v) => (v ? 'Oui' : 'Non'),
    },
    // V2 R4 Phase B
    has_vehicle: {
      label: 'Possède un véhicule',
      formatValue: (v) => (v ? 'Oui' : 'Non'),
    },

    // Notes et rapport
    notes: {
      label: 'Notes',
      formatValue: (v) => (v ? 'Modifiées' : 'Supprimées'),
    },
    report: {
      label: 'Rapport',
      formatValue: (v) => (v ? 'Modifié' : 'Supprimé'),
    },
  },
  statusDetectors: [
    {
      name: 'Équipage terminé',
      priority: 100,
      detect: (oldData, newData) => {
        // Détecter le passage automatique vers completed
        return oldData.status === 'bed_complete' && newData.status === 'completed';
      },
      description: () => 'Équipage terminé (produit terminé + BED complet)',
    },
    {
      name: 'Changement de statut',
      priority: 50,
      detect: (oldData, newData) => {
        return oldData.status !== newData.status;
      },
      description: (oldData, newData) => {
        const oldLabel =
          CREW_STATUS_LABELS[oldData.status as keyof typeof CREW_STATUS_LABELS] || oldData.status;
        const newLabel =
          CREW_STATUS_LABELS[newData.status as keyof typeof CREW_STATUS_LABELS] || newData.status;
        return `Statut changé de "${oldLabel}" à "${newLabel}"`;
      },
    },
  ],
};

// =====================================================
// CONFIGURATION CONTACT
// =====================================================

export const CONTACT_LOGGING_CONFIG: EntityLoggingConfig = {
  entityType: 'contact',
  trackedFields: {
    // Champs de statut
    status: {
      label: 'Statut',
      isStatus: true,
      formatValue: (v) => CONTACT_STATUS_LABELS[v as keyof typeof CONTACT_STATUS_LABELS] || v,
    },

    // Informations personnelles
    first_name: {
      label: 'Prénom',
    },
    last_name: {
      label: 'Nom',
    },
    email: {
      label: 'Email',
    },
    phone: {
      label: 'Téléphone',
      formatValue: (v) => v || 'Non renseigné',
    },

    // Relations
    origin_id: {
      label: 'Origine',
      formatValue: (v, data) => {
        if (!v) return 'Aucune';
        return data?.origin?.label || data?.origin?.name || v;
      },
    },
    // assigned_to: géré manuellement via logAssignedUserChange dans useCRUD
    // pour avoir accès aux noms d'utilisateurs complets

    // Notes
    notes: {
      label: 'Notes',
      formatValue: (v) => (v ? 'Modifiées' : 'Supprimées'),
    },
  },
  statusDetectors: [
    {
      name: 'Contact terminé',
      priority: 100,
      detect: (oldData, newData) => {
        return oldData.status !== 'completed' && newData.status === 'completed';
      },
      description: (oldData) => {
        const oldStatus =
          CONTACT_STATUS_LABELS[oldData.status as keyof typeof CONTACT_STATUS_LABELS];
        return `Progression de ${oldStatus} à ${CONTACT_STATUS_LABELS.completed}`;
      },
    },
  ],
};

// =====================================================
// CONFIGURATION PROSPECT (FormSubmission)
// =====================================================

export const PROSPECT_LOGGING_CONFIG: EntityLoggingConfig = {
  entityType: 'prospect',
  trackedFields: {
    // Les prospects n'ont pas vraiment de champs directs à logger
    // car les données sont dans le JSON "data"
    // On pourrait logger les changements de contact associé
    contact_id: {
      label: 'Contact associé',
    },
    form_id: {
      label: 'Formulaire',
    },
  },
};

// =====================================================
// CONFIGURATION DES AUTRES ENTITÉS (optionnelles)
// =====================================================

// Pour les entités non critiques, on peut avoir des configs minimalistes
// ou ne pas les logger du tout

export const VEHICLE_LOGGING_CONFIG: EntityLoggingConfig = {
  entityType: 'crew', // Les véhicules sont liés aux crews
  trackedFields: {
    registration: {
      label: 'Immatriculation',
    },
    model: {
      label: 'Modèle',
    },
  },
};

export const SCHOOL_LOGGING_CONFIG: EntityLoggingConfig = {
  entityType: 'crew', // Les écoles sont liées aux crews
  trackedFields: {
    name: {
      label: 'Nom',
    },
  },
};

export const ASSOCIATION_LOGGING_CONFIG: EntityLoggingConfig = {
  entityType: 'crew', // Les associations sont liées aux crews
  trackedFields: {
    name: {
      label: 'Nom',
    },
  },
};

export const DISCOUNT_LOGGING_CONFIG: EntityLoggingConfig = {
  entityType: 'crew', // Les remises sont liées aux crews
  trackedFields: {
    name: {
      label: 'Nom',
    },
    amount: {
      label: 'Montant',
      formatValue: (v) => `${v}€`,
    },
  },
};

export const PRODUCT_LOGGING_CONFIG: EntityLoggingConfig = {
  entityType: 'crew', // Les produits sont liés aux crews
  trackedFields: {
    name: {
      label: 'Nom',
    },
  },
};

// =====================================================
// MAP GLOBAL DE CONFIGURATION
// =====================================================

export const ENTITY_LOGGING_CONFIGS: Record<string, EntityLoggingConfig> = {
  crew: CREW_LOGGING_CONFIG,
  contact: CONTACT_LOGGING_CONFIG,
  prospect: PROSPECT_LOGGING_CONFIG,
  // Les autres entités peuvent être ajoutées si nécessaire
  // vehicle: VEHICLE_LOGGING_CONFIG,
  // school: SCHOOL_LOGGING_CONFIG,
  // association: ASSOCIATION_LOGGING_CONFIG,
  // discount: DISCOUNT_LOGGING_CONFIG,
  // product: PRODUCT_LOGGING_CONFIG,
};
